<template>
  <div class="site-wrap" :class="[guideThemeClass, { 'site-wrap--legal': isLegalPage }]">
    <HeadNav selectedLanguage="en" :page="content.navPage || 'page'" />

    <main class="page-main" :class="{ 'page-main--legal': isLegalPage }">
      <nav v-if="content.isGuide" class="guide-breadcrumb" aria-label="Breadcrumb">
        <a href="/">Home</a>
        <span aria-hidden="true">/</span>
        <a :href="content.breadcrumbParent.href">{{ content.breadcrumbParent.label }}</a>
        <span aria-hidden="true">/</span>
        <span>{{ content.breadcrumbLabel }}</span>
      </nav>

      <section
        v-if="content.navPage !== 'legal'"
        class="page-hero"
        :class="{ 'guide-hero': content.isGuide, 'has-hero-icon': content.heroIcon }"
      >
        <div class="hero-copy">
          <span class="eyebrow">{{ content.guideBadge || content.navPage || 'Guide' }}</span>
          <h1>{{ content.title }}</h1>
          <p>{{ content.intro }}</p>
          <a
            v-if="content.heroCta && content.heroCta.download"
            href="#"
            class="hero-download-btn"
            @click.prevent="handleHeroDownload"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M9 2v9M9 11l3.5-3.5M9 11L5.5 7.5M3 14h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {{ content.heroCta.text }}
          </a>
          <div v-if="content.isGuide" class="hero-meta">
            <span v-for="tag in heroMetaTags" :key="tag">{{ tag }}</span>
          </div>
        </div>
        <aside class="hero-card" :class="{ 'hero-card--visual': content.heroIcon }">
          <div v-if="content.heroIcon" class="hero-feature-icon" aria-hidden="true">
            <img :src="content.heroIcon" alt="" width="72" height="72" loading="lazy">
          </div>
          <template v-else>
            <strong>Quick path</strong>
            <a href="/download/">Download GB WhatsApp APK</a>
            <a href="/tutorials/">Open GB WhatsApp tutorials</a>
            <a href="/features/">Explore GB WhatsApp features</a>
          </template>
        </aside>
      </section>

      <section
        v-if="content.isGuide && content.scenarios && content.scenarios.length"
        class="guide-scenarios"
      >
        <div class="guide-scenarios-icon" aria-hidden="true">
          <img
            :src="content.heroIcon || require('@/assets/tutorials/hub-hero.svg')"
            alt=""
            width="40"
            height="40"
            loading="lazy"
          >
        </div>
        <div class="guide-scenarios-copy">
          <strong>This guide helps if you:</strong>
          <ul>
            <li v-for="(item, itemIndex) in content.scenarios" :key="textKey(item, itemIndex)">
              <template v-if="isRichText(item)">
                <component
                  :is="part.href ? 'a' : 'span'"
                  v-for="(part, partIndex) in item.parts"
                  :key="`scenario-${itemIndex}-${partIndex}`"
                  :href="part.href || null"
                  :class="richTextPartClass(part)"
                  v-text="part.text"
                />
              </template>
              <template v-else>{{ item }}</template>
            </li>
          </ul>
        </div>
      </section>

      <section
        v-if="content.isGuide && content.openingParagraphs && content.openingParagraphs.length"
        class="guide-opening"
      >
        <p v-for="(paragraph, paragraphIndex) in content.openingParagraphs" :key="textKey(paragraph, paragraphIndex)">
          <template v-if="isRichText(paragraph)">
            <component
              :is="part.href ? 'a' : 'span'"
              v-for="(part, partIndex) in paragraph.parts"
              :key="`opening-${paragraphIndex}-${partIndex}`"
              :href="part.href || null"
              :class="richTextPartClass(part)"
              v-text="part.text"
            />
          </template>
          <template v-else>{{ paragraph }}</template>
        </p>
      </section>

      <div v-if="content.isGuide" class="guide-layout">
        <aside class="guide-toc" aria-label="Table of contents">
          <strong>On this page</strong>
          <a
            v-for="(section, index) in content.sections"
            :key="section.heading"
            :href="'#guide-step-' + index"
          >
            <span class="toc-step">{{ section.step || formatStep(index) }}</span>
            {{ section.heading }}
          </a>
        </aside>

        <section class="guide-steps">
          <article
            v-for="(section, index) in content.sections"
            :id="'guide-step-' + index"
            :key="section.heading"
            class="guide-step-card"
          >
            <div class="guide-step-head">
              <span class="guide-step-no">{{ section.step || formatStep(index) }}</span>
              <div v-if="section.iconSrc" class="guide-step-icon">
                <img :src="section.iconSrc" :alt="section.heading" width="36" height="36" loading="lazy">
              </div>
              <h2>{{ section.heading }}</h2>
            </div>
            <div class="guide-step-body">
              <p v-if="section.scene" class="guide-step-scene">{{ section.scene }}</p>
              <p v-for="(paragraph, paragraphIndex) in section.body" :key="textKey(paragraph, paragraphIndex)">
                <template v-if="isRichText(paragraph)">
                  <component
                    :is="part.href ? 'a' : 'span'"
                    v-for="(part, partIndex) in paragraph.parts"
                    :key="`guide-paragraph-${paragraphIndex}-${partIndex}`"
                    :href="part.href || null"
                    :class="richTextPartClass(part)"
                    v-text="part.text"
                  />
                </template>
                <template v-else>{{ paragraph }}</template>
              </p>
              <ul v-if="section.bullets && section.bullets.length" class="guide-step-list">
                <li v-for="(item, itemIndex) in section.bullets" :key="textKey(item, itemIndex)">
                  <template v-if="isRichText(item)">
                    <component
                      :is="part.href ? 'a' : 'span'"
                      v-for="(part, partIndex) in item.parts"
                      :key="`guide-bullet-${itemIndex}-${partIndex}`"
                      :href="part.href || null"
                      :class="richTextPartClass(part)"
                      v-text="part.text"
                    />
                  </template>
                  <template v-else>{{ item }}</template>
                </li>
              </ul>
              <div v-if="section.avoid && section.avoid.length" class="guide-step-avoid">
                <strong>Avoid this</strong>
                <ul>
                  <li v-for="(item, itemIndex) in section.avoid" :key="textKey(item, itemIndex)">
                    <template v-if="isRichText(item)">
                      <component
                        :is="part.href ? 'a' : 'span'"
                        v-for="(part, partIndex) in item.parts"
                        :key="`guide-avoid-${itemIndex}-${partIndex}`"
                        :href="part.href || null"
                        :class="richTextPartClass(part)"
                        v-text="part.text"
                      />
                    </template>
                    <template v-else>{{ item }}</template>
                  </li>
                </ul>
              </div>
              <div v-if="section.tip" class="guide-step-tip">
                <strong>Pro tip</strong>
                <p>{{ section.tip }}</p>
              </div>
            </div>
            <span v-if="index < content.sections.length - 1" class="guide-step-connector" aria-hidden="true" />
          </article>
        </section>
      </div>

      <section v-else class="content-grid" :class="{ 'content-grid--legal': isLegalPage }">
        <article class="content-card" v-for="section in content.sections" :key="section.cta ? section.cta.href : section.heading">
          <h2 v-if="section.cta" class="card-title-link">
            <a :href="section.cta.href">{{ section.cta.text }}</a>
          </h2>
          <h2 v-else-if="content.navPage !== 'legal'">{{ section.heading }}</h2>
          <p v-for="(paragraph, paragraphIndex) in section.body" :key="textKey(paragraph, paragraphIndex)">
            <template v-if="isRichText(paragraph)">
              <component
                :is="part.href ? 'a' : 'span'"
                v-for="(part, partIndex) in paragraph.parts"
                :key="`part-${paragraphIndex}-${partIndex}`"
                :href="part.href || null"
                :class="richTextPartClass(part)"
                v-text="part.text"
              />
            </template>
            <template v-else>{{ paragraph }}</template>
          </p>

          <div v-if="section.table" class="info-table">
            <div v-for="row in section.table" :key="row[0]" class="info-row">
              <span>{{ row[0] }}</span>
              <strong>{{ row[1] }}</strong>
            </div>
          </div>
        </article>
      </section>

      <section v-if="content.isGuide" class="guide-bottom-cta">
        <div>
          <h2>{{ bottomCta.title }}</h2>
          <p>{{ bottomCta.text }}</p>
        </div>
        <a :href="bottomCta.href" class="guide-bottom-btn">{{ bottomCta.button }}</a>
      </section>

      <section
        v-if="content.relatedLinks && content.relatedLinks.length"
        class="section-panel guide-related"
      >
        <h2>Related GB WhatsApp guides</h2>
        <nav class="guide-related-nav" aria-label="Related GB WhatsApp guides">
          <h3 v-for="link in content.relatedLinks" :key="link.href" class="title-link">
            <a :href="link.href">{{ link.text }}</a>
          </h3>
        </nav>
      </section>

      <section v-if="content.faqs && content.faqs.length" class="section-panel faq">
        <h2>Frequently Asked Questions (FAQ)</h2>
        <details v-for="item in content.faqs" :key="item.q">
          <summary>{{ item.q }}</summary>
          <p>{{ item.a }}</p>
        </details>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import HeadNav from '@/components/HeadNav.vue';
import { sitePages } from '@/data/sitePages';

export default {
  name: 'SitePage',
  components: { HeadNav },
  data() {
    return {
      apk: null,
    };
  },
  computed: {
    content() {
      return sitePages[this.$route.path] || sitePages['/tutorials/'];
    },
    heroMetaTags() {
      if (this.content.heroMeta && this.content.heroMeta.length) {
        return this.content.heroMeta;
      }
      return [
        `${this.content.sections.length} steps`,
        'Android APK guide',
        'Updated 2026',
      ];
    },
    bottomCta() {
      return this.content.bottomCta || {
        title: 'Ready to install GB WhatsApp?',
        text: 'Download the latest APK and follow the steps above for a safe setup.',
        button: 'Download GB WhatsApp APK',
        href: '/download/',
      };
    },
    guideThemeClass() {
      return this.content.guideTheme ? `guide-theme-${this.content.guideTheme}` : '';
    },
    isLegalPage() {
      return this.content.navPage === 'legal';
    },
  },
  mounted() {
    if (this.content.heroCta && this.content.heroCta.download) {
      this.fetchApkLink();
    }
  },
  methods: {
    formatStep(index) {
      return String(index + 1).padStart(2, '0');
    },
    isRichText(value) {
      return value && Array.isArray(value.parts);
    },
    richTextPartClass(part) {
      return {
        'rich-text-bold': part.bold,
        'rich-text-link': part.href,
      };
    },
    textKey(value, index) {
      if (typeof value === 'string') {
        return value;
      }
      return `text-${index}`;
    },
    fetchApkLink() {
      const from = this.$global.GetQueryString('from');
      if (from) {
        this.$server
          .getAPPInfo({
            promotionChannel: from,
            promotionName: this.$global.GetQueryString('filename') || 'gb',
          })
          .then((res) => {
            this.apk = res.code === 200 ? res.data.apk : process.env.VUE_APP_GBLINK;
          });
        return;
      }

      this.$server
        .getOfficalApk({
          domain: 'gbapp.dev',
          appName: 'GB_gbapp.dev',
        })
        .then((res) => {
          this.apk = res.code === 200 ? res.data.apk : process.env.VUE_APP_GBLINK;
        });
    },
    handleHeroDownload() {
      if (this.apk) {
        this.$global.download(this.apk);
        this.$logEvent('download_from_hero', 'download');
        return;
      }

      this.fetchApkLink();
      setTimeout(() => {
        if (this.apk) {
          this.$global.download(this.apk);
          this.$logEvent('download_from_hero', 'download');
        }
      }, 600);
    },
  },
};
</script>

<style lang="scss" scoped>
.site-wrap {
  --guide-page-bg: #f5fbf8;
  --guide-text: #123c32;
  --guide-link: #07865a;
  --guide-link-hover: #065f46;
  --guide-hero-start: rgba(8, 96, 75, 0.94);
  --guide-hero-end: rgba(14, 162, 108, 0.9);
  --guide-hero-shadow: rgba(7, 86, 63, 0.2);
  --guide-hero-glow: rgba(61, 255, 138, 0.18);
  --guide-accent: #16a34a;
  --guide-accent-light: #22c55e;
  --guide-accent-shadow: rgba(22, 163, 74, 0.28);
  --guide-scene-bg: #ecfdf5;
  --guide-scene-text: #047857;
  --guide-icon-bg-start: #f0fff6;
  --guide-icon-bg-end: #e8fff3;
  --guide-icon-border: rgba(22, 163, 74, 0.12);
  --guide-toc-hover: #effaf5;
  --guide-scenarios-bg-end: #f3fcf8;
  --guide-cta-btn-start: #3dff8a;
  --guide-cta-btn-end: #16a34a;
  --guide-connector: #86efac;
  --guide-tip-border: #22c55e;
  --guide-tip-bg: #f0fff6;
  --guide-tip-text: #15803d;
  --guide-title-link: #065f46;
  --guide-heading: #073f32;

  min-height: 100vh;
  background: var(--guide-page-bg);
  color: var(--guide-text);
}

.guide-theme-privacy {
  --guide-page-bg: #f0fdfa;
  --guide-link: #0891b2;
  --guide-link-hover: #0e7490;
  --guide-hero-start: rgba(6, 95, 115, 0.95);
  --guide-hero-end: rgba(8, 145, 178, 0.92);
  --guide-hero-shadow: rgba(8, 145, 178, 0.24);
  --guide-hero-glow: rgba(103, 232, 249, 0.24);
  --guide-accent: #0891b2;
  --guide-accent-light: #22d3ee;
  --guide-accent-shadow: rgba(8, 145, 178, 0.28);
  --guide-scene-bg: #cffafe;
  --guide-scene-text: #0e7490;
  --guide-icon-bg-start: #ecfeff;
  --guide-icon-bg-end: #cffafe;
  --guide-icon-border: rgba(8, 145, 178, 0.16);
  --guide-toc-hover: #ecfeff;
  --guide-scenarios-bg-end: #ecfeff;
  --guide-cta-btn-start: #67e8f9;
  --guide-cta-btn-end: #0891b2;
  --guide-connector: #67e8f9;
  --guide-tip-border: #06b6d4;
  --guide-tip-bg: #ecfeff;
  --guide-tip-text: #0e7490;
  --guide-title-link: #0e7490;
  --guide-heading: #164e63;
}

.guide-theme-anti-delete {
  --guide-page-bg: #fff1f2;
  --guide-link: #e11d48;
  --guide-link-hover: #be123c;
  --guide-hero-start: rgba(136, 19, 55, 0.94);
  --guide-hero-end: rgba(225, 29, 72, 0.9);
  --guide-hero-shadow: rgba(190, 18, 60, 0.24);
  --guide-hero-glow: rgba(251, 113, 133, 0.22);
  --guide-accent: #e11d48;
  --guide-accent-light: #fb7185;
  --guide-accent-shadow: rgba(225, 29, 72, 0.28);
  --guide-scene-bg: #ffe4e6;
  --guide-scene-text: #be123c;
  --guide-icon-bg-start: #fff1f2;
  --guide-icon-bg-end: #ffe4e6;
  --guide-icon-border: rgba(225, 29, 72, 0.14);
  --guide-toc-hover: #fff1f2;
  --guide-scenarios-bg-end: #fff1f2;
  --guide-cta-btn-start: #fb7185;
  --guide-cta-btn-end: #e11d48;
  --guide-connector: #fda4af;
  --guide-tip-border: #f43f5e;
  --guide-tip-bg: #fff1f2;
  --guide-tip-text: #be123c;
  --guide-title-link: #be123c;
  --guide-heading: #881337;
}

.guide-theme-themes {
  --guide-page-bg: #faf5ff;
  --guide-link: #7c3aed;
  --guide-link-hover: #6d28d9;
  --guide-hero-start: rgba(76, 29, 149, 0.94);
  --guide-hero-end: rgba(124, 58, 237, 0.9);
  --guide-hero-shadow: rgba(109, 40, 217, 0.24);
  --guide-hero-glow: rgba(196, 181, 253, 0.24);
  --guide-accent: #7c3aed;
  --guide-accent-light: #a78bfa;
  --guide-accent-shadow: rgba(124, 58, 237, 0.28);
  --guide-scene-bg: #ede9fe;
  --guide-scene-text: #6d28d9;
  --guide-icon-bg-start: #f5f3ff;
  --guide-icon-bg-end: #ede9fe;
  --guide-icon-border: rgba(124, 58, 237, 0.14);
  --guide-toc-hover: #f5f3ff;
  --guide-scenarios-bg-end: #f5f3ff;
  --guide-cta-btn-start: #c4b5fd;
  --guide-cta-btn-end: #7c3aed;
  --guide-connector: #c4b5fd;
  --guide-tip-border: #8b5cf6;
  --guide-tip-bg: #f5f3ff;
  --guide-tip-text: #6d28d9;
  --guide-title-link: #6d28d9;
  --guide-heading: #4c1d95;
}

.guide-theme-dual {
  --guide-page-bg: #eff6ff;
  --guide-link: #2563eb;
  --guide-link-hover: #1d4ed8;
  --guide-hero-start: rgba(30, 64, 175, 0.94);
  --guide-hero-end: rgba(37, 99, 235, 0.9);
  --guide-hero-shadow: rgba(37, 99, 235, 0.24);
  --guide-hero-glow: rgba(147, 197, 253, 0.24);
  --guide-accent: #2563eb;
  --guide-accent-light: #60a5fa;
  --guide-accent-shadow: rgba(37, 99, 235, 0.28);
  --guide-scene-bg: #dbeafe;
  --guide-scene-text: #1d4ed8;
  --guide-icon-bg-start: #eff6ff;
  --guide-icon-bg-end: #dbeafe;
  --guide-icon-border: rgba(37, 99, 235, 0.14);
  --guide-toc-hover: #eff6ff;
  --guide-scenarios-bg-end: #eff6ff;
  --guide-cta-btn-start: #93c5fd;
  --guide-cta-btn-end: #2563eb;
  --guide-connector: #93c5fd;
  --guide-tip-border: #3b82f6;
  --guide-tip-bg: #eff6ff;
  --guide-tip-text: #1d4ed8;
  --guide-title-link: #1d4ed8;
  --guide-heading: #1e3a8a;
}

.page-main {
  max-width: 1120px;
  margin: 0 auto;
  padding: 28px 20px 56px;
}

.site-wrap--legal {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.page-main--legal {
  flex: 1;
  padding: 16px 20px;
  box-sizing: border-box;
}

.content-grid--legal {
  margin-top: 0;
}

.page-main--legal .content-card {
  padding: 28px 26px;

  p:last-child {
    margin-bottom: 0;
  }
}

.guide-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 600;

  a {
    color: var(--guide-link);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  span:last-child {
    color: #5a7a70;
  }

  span[aria-hidden='true'] {
    color: #9cb5ad;
  }
}

.page-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 22px;
  align-items: stretch;
  padding: 34px;
  border-radius: 16px;
  background:
    linear-gradient(135deg, var(--guide-hero-start), var(--guide-hero-end)),
    url('../assets/gbwhatsapp-site-pic.webp') center/cover;
  color: #ffffff;
  box-shadow: 0 18px 44px var(--guide-hero-shadow);

  h1 {
    margin: 10px 0 12px;
    max-width: 820px;
    font-size: 42px;
    line-height: 1.08;
    color: #ffffff;
  }

  p {
    margin: 0;
    max-width: 760px;
    color: rgba(255, 255, 255, 0.88);
    font-size: 17px;
    line-height: 1.7;
  }
}

.hero-download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 22px;
  min-height: 52px;
  padding: 0 28px;
  border-radius: 999px;
  background: linear-gradient(180deg, #3dff8a 0%, #16a34a 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.28);
  }
}

.guide-hero {
  border-radius: 20px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 85% 20%, var(--guide-hero-glow), transparent 40%);
    pointer-events: none;
  }

  .hero-copy {
    position: relative;
    z-index: 1;
  }
}

.page-hero {
  position: relative;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;

  span {
    padding: 6px 12px;
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    font-size: 12px;
    font-weight: 700;
  }
}

.eyebrow {
  display: inline-flex;
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.hero-card {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 10px;
  align-content: center;
  padding: 18px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(6px);

  strong {
    color: #ffffff;
    font-size: 18px;
  }

  a {
    color: #eafff5;
    text-decoration: none;
    font-weight: 700;

    &:hover {
      color: #ffffff;
    }
  }
}

.hero-card--visual {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

.hero-feature-icon {
  width: 112px;
  height: 112px;
  display: grid;
  place-items: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);

  img {
    width: 72px;
    height: 72px;
    object-fit: contain;
  }
}

.guide-scenarios {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 18px;
  align-items: start;
  margin-top: 22px;
  padding: 20px 22px;
  border: 1px solid rgba(18, 111, 83, 0.14);
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, var(--guide-scenarios-bg-end) 100%);
  box-shadow: 0 10px 26px rgba(7, 86, 63, 0.06);
}

.guide-scenarios-icon {
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: linear-gradient(180deg, var(--guide-icon-bg-start) 0%, var(--guide-icon-bg-end) 100%);
  box-shadow: inset 0 0 0 1px var(--guide-icon-border);

  img {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }
}

.guide-scenarios-copy {
  strong {
    display: block;
    margin-bottom: 10px;
    color: var(--guide-heading);
    font-size: 15px;
    font-weight: 800;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    color: #3e665c;
    font-size: 15px;
    line-height: 1.65;
  }

  li + li {
    margin-top: 6px;
  }
}

.guide-opening {
  width: 100%;
  margin: 22px 0 0;
  box-sizing: border-box;

  p {
    margin: 0 0 14px;
    color: #335f55;
    font-size: 17px;
    line-height: 1.75;
  }
}

.rich-text-bold {
  color: #202832;
  font-weight: 800;
}

.rich-text-link {
  color: #1769e0 !important;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    color: #0f55c7 !important;
    text-decoration: underline;
  }
}

.guide-layout {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 24px;
  margin-top: 24px;
  align-items: start;
}

.guide-toc {
  position: sticky;
  top: 88px;
  padding: 18px;
  border: 1px solid rgba(18, 111, 83, 0.12);
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 10px 26px rgba(7, 86, 63, 0.07);

  strong {
    display: block;
    margin-bottom: 12px;
    color: var(--guide-heading);
    font-size: 14px;
    font-weight: 800;
  }

  a {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    padding: 10px 8px;
    border-radius: 10px;
    color: #335f55;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease;

    &:hover {
      background: var(--guide-toc-hover);
      color: var(--guide-link-hover);
    }
  }

  .toc-step {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, var(--guide-accent-light), var(--guide-accent));
    color: #ffffff;
    font-size: 11px;
    font-weight: 800;
  }
}

.guide-steps {
  display: grid;
  gap: 0;
}

.guide-step-card {
  position: relative;
  padding: 24px 24px 28px 24px;
  border: 1px solid rgba(18, 111, 83, 0.12);
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(7, 86, 63, 0.08);

  & + & {
    margin-top: 20px;
  }
}

.guide-step-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.guide-step-no {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--guide-accent-light), var(--guide-accent));
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  box-shadow: 0 8px 18px var(--guide-accent-shadow);
}

.guide-step-icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: linear-gradient(180deg, var(--guide-icon-bg-start) 0%, var(--guide-icon-bg-end) 100%);
  box-shadow: inset 0 0 0 1px var(--guide-icon-border);

  img {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }
}

.guide-step-head h2 {
  margin: 0;
  color: var(--guide-heading);
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
}

.guide-step-body {
  padding-left: 54px;

  p {
    margin: 0 0 12px;
    color: #3e665c;
    font-size: 16px;
    line-height: 1.75;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.guide-step-scene {
  display: inline-block;
  margin: 0 0 12px !important;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--guide-scene-bg);
  color: var(--guide-scene-text) !important;
  font-size: 13px !important;
  font-weight: 700;
  line-height: 1.4 !important;
}

.guide-step-list {
  margin: 14px 0 0;
  padding-left: 20px;
  color: #3e665c;
  font-size: 15px;
  line-height: 1.7;

  li {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.guide-step-avoid {
  margin-top: 14px;
  padding: 14px 16px;
  border-left: 4px solid #f59e0b;
  border-radius: 0 12px 12px 0;
  background: linear-gradient(90deg, #fffbeb 0%, #ffffff 100%);

  strong {
    display: block;
    margin-bottom: 8px;
    color: #b45309;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    color: #7c5f2a;
    font-size: 14px;
    line-height: 1.65;
  }

  li + li {
    margin-top: 6px;
  }
}

.guide-step-tip {
  margin-top: 16px;
  padding: 14px 16px;
  border-left: 4px solid var(--guide-tip-border);
  border-radius: 0 12px 12px 0;
  background: linear-gradient(90deg, var(--guide-tip-bg) 0%, #ffffff 100%);

  strong {
    display: block;
    margin-bottom: 6px;
    color: var(--guide-tip-text);
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    color: #335f55;
    font-size: 14px;
    line-height: 1.65;
  }
}

.guide-step-connector {
  position: absolute;
  left: 42px;
  bottom: -20px;
  width: 2px;
  height: 20px;
  background: linear-gradient(180deg, var(--guide-connector), transparent);
  z-index: 1;
}

.guide-bottom-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 28px;
  padding: 28px 32px;
  border-radius: 18px;
  background: linear-gradient(135deg, #031612 0%, #052018 50%, #0a3326 100%);
  box-shadow: 0 18px 40px rgba(3, 22, 18, 0.28);

  h2 {
    margin: 0 0 8px;
    color: #ffffff;
    font-size: 24px;
    font-weight: 800;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 15px;
    line-height: 1.6;
  }
}

.guide-bottom-btn {
  flex-shrink: 0;
  min-height: 52px;
  padding: 0 28px;
  border-radius: 999px;
  background: linear-gradient(180deg, var(--guide-cta-btn-start) 0%, var(--guide-cta-btn-end) 100%);
  box-shadow: 0 10px 28px var(--guide-accent-shadow);
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 22px;
}

.content-card {
  border: 1px solid rgba(18, 111, 83, 0.12);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 26px rgba(7, 86, 63, 0.07);
  padding: 22px;

  h2 {
    margin: 0 0 14px;
    color: #074b39;
    font-size: 24px;
    line-height: 1.2;
  }

  .card-title-link {
    margin: 0 0 14px;

    a {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      color: #065f46;
      font-size: 24px;
      font-weight: 800;
      line-height: 1.3;
      text-decoration: underline;
      text-decoration-color: rgba(22, 163, 74, 0.45);
      text-underline-offset: 4px;
      transition: color 0.2s ease, text-decoration-color 0.2s ease;

      &::after {
        content: '→';
        flex-shrink: 0;
        color: #16a34a;
        font-size: 22px;
        font-weight: 900;
        text-decoration: none;
      }

      &:hover {
        color: #16a34a;
        text-decoration-color: #16a34a;
      }
    }
  }

  p {
    margin: 0 0 12px;
    color: #335f55;
    font-size: 16px;
    line-height: 1.75;
  }
}

.info-table {
  display: grid;
  gap: 8px;
  margin-top: 14px;
}

.info-row {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  background: #effaf5;

  span {
    color: #5b8378;
  }

  strong {
    color: #073f32;
  }
}

.section-panel {
  width: 100%;
  box-sizing: border-box;
  margin-top: 22px;
  padding: 28px;
  border: 1px solid rgba(18, 111, 83, 0.12);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 26px rgba(7, 86, 63, 0.07);

  h2 {
    margin: 0 0 12px;
    color: var(--guide-heading);
    font-size: 34px;
    line-height: 1.12;
    font-weight: 900;
  }

  p {
    margin: 0;
    color: #3e665c;
    line-height: 1.75;
    font-size: 16px;
  }
}

.guide-related-nav {
  display: grid;
  gap: 10px;
  margin-top: 6px;
}

.title-link {
  margin: 0;

  a {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: var(--guide-title-link);
    font-size: 18px;
    font-weight: 800;
    line-height: 1.35;
    text-decoration: underline;
    text-decoration-color: color-mix(in srgb, var(--guide-accent) 45%, transparent);
    text-underline-offset: 4px;
    transition: color 0.2s ease, text-decoration-color 0.2s ease;

    &::after {
      content: '→';
      flex-shrink: 0;
      color: var(--guide-accent);
      font-size: 20px;
      font-weight: 900;
      text-decoration: none;
    }

    &:hover {
      color: var(--guide-accent);
      text-decoration-color: var(--guide-accent);
    }
  }
}

.faq details {
  border-top: 1px solid rgba(10, 111, 77, 0.12);
  padding: 14px 0;
}

.faq summary {
  cursor: pointer;
  color: #073f32;
  font-weight: 900;
}

@media (max-width: 820px) {
  .page-main {
    padding: 18px 14px 42px;
  }

  .page-main--legal {
    padding: 12px 14px;
  }

  .page-main--legal .content-card {
    padding: 20px 18px;
  }

  .page-hero,
  .content-grid,
  .guide-layout {
    grid-template-columns: 1fr;
  }

  .page-hero {
    padding: 24px 18px;

    h1 {
      font-size: 30px;
    }
  }

  .hero-download-btn {
    width: 100%;
    margin-top: 18px;
    min-height: 50px;
    font-size: 15px;
  }

  .guide-scenarios {
    grid-template-columns: 1fr;
  }

  .guide-toc {
    position: static;
    display: none;
  }

  .guide-step-body {
    padding-left: 0;
  }

  .guide-step-connector {
    display: none;
  }

  .guide-bottom-cta {
    flex-direction: column;
    align-items: stretch;
    padding: 22px 20px;
    text-align: center;
  }

  .guide-bottom-btn {
    width: 100%;
  }

  .info-row {
    grid-template-columns: 1fr;
  }

  .section-panel h2 {
    font-size: 28px;
  }
}
</style>
